import { CATALOGUE_MODE } from "@constants";
import { useQuery } from "@tanstack/react-query";

import { useDispatch } from "react-redux";

import { getFontConfig } from "@helpers/fontConfig";

import { useLocalWays } from "@hooks/useLocalWays";
import { fetchProjectData } from "@hooks/useProject";

import {
    resetFontCache,
    setOrderTakingSetting,
} from "@components/OrderTaking/Helpers/Commonfunction";
import { setSetting } from "@components/OrderTaking/Store/feature";

import {
    setActiveConfigOptions,
    setActiveLanguages,
    setCustomerLanguage,
    setInitialStepTimeout,
    setIsConfigOptionsActive,
    setIsLanguagesActive,
    setIsProjectMustBeUpdated,
    setKioskWays,
    setLogo,
    setProject,
    setProjectTimeout,
    setSelectedSaleModeOrderTaking,
} from "@pages/Kiosk/store";

export async function fetchProject(): Promise<ProjectType | null> {
    return await fetchProjectData();
}

export function useSteps(): void {
    if (process.env.REACT_APP_MODE === CATALOGUE_MODE) {
        return;
    }
    const {
        isLoading,
        data: project,
        error,
    } = useQuery({
        queryFn: fetchProject,
        refetchOnWindowFocus: false,
        retry: 1,
        cacheTime: 0,
    });

    if (isLoading) {
        // Handle loading state, like showing a spinner or message
        setIsProjectMustBeUpdated({
            setting: true,
            peripheral: false,
        });
        return;
    }

    // If there was an error, you can handle it here (optional)
    if (error) {
        console.error("Error fetching project data:", error);
        return;
    }
    useKioskProject(project as ProjectType);
    // Now that the data is available, proceed with the setup
}

export function useKioskProject(project: ProjectType): void {
    const dispatch = useDispatch();
    const localKioskWays = useLocalWays(project as ProjectType);

    // Drop the previous project's cached font values before reading the new
    // config below; otherwise getFontConfig() can return stale styles.
    resetFontCache();

    setKioskWays(localKioskWays);

    setProjectTimeout((project as ProjectType).template.timeout);

    setIsConfigOptionsActive(
        (
            ((project as ProjectType).template.content as ProjectContentType)
                .options as OptionsType
        ).active
    );

    setActiveConfigOptions(
        (
            ((project as ProjectType).template.content as ProjectContentType)
                .options as OptionsType
        ).items
            .filter((option) => option.active === true)
            .filter(
                (option) =>
                    process.env.REACT_APP_MODE === CATALOGUE_MODE &&
                    option.title.toLowerCase() !== "prm/child" &&
                    option.title.toLowerCase() !== "usual mode"
            )
    );

    setIsLanguagesActive(
        (
            ((project as ProjectType).template.content as ProjectContentType)
                .languages as LanguagesType
        ).active &&
            (
                (
                    (project as ProjectType).template
                        .content as ProjectContentType
                ).languages as LanguagesType
            )?.items.filter((option) => option.active === true).length > 1
    );

    setActiveLanguages(
        (
            ((project as ProjectType).template.content as ProjectContentType)
                .languages as LanguagesType
        ).items.filter((language) => language.active === true)
    );

    setCustomerLanguage(
        (
            (project as ProjectType).template.content.languages as LanguagesType
        ).items.filter((language) => language.isDefault === true)[0]
    );

    setInitialStepTimeout(
        (project as ProjectType).template.initialStepTimeout as number
    );

    setLogo(
        (
            (project as ProjectType).template.content
                .generalDesign as GeneralDesignType
        ).logo
    );

    dispatch(
        setSetting(
            setOrderTakingSetting(
                (project as ProjectType).template.pages.ways
                    .orderTaking as PageType,
                (
                    (project as ProjectType).template.content
                        .generalDesign as GeneralDesignType
                ).colors[0].content,
                (
                    (project as ProjectType).template.content
                        .generalDesign as GeneralDesignType
                ).colors[1].content,
                getFontConfig()
            )
        )
    );

    if (
        ((
            (project as ProjectType).template.pages.ways[
                "salesMethods"
            ] as PageType
        ).skipped as boolean) === true
    ) {
        const activeSalesMode = (
            (project as ProjectType).template.content
                .salesMethods as ProjectContentItemType
        ).items.filter((item) => item.active === true);
        if (activeSalesMode.length === 1) {
            setSelectedSaleModeOrderTaking(
                activeSalesMode[0].settingKey as string
            );
        }
    }
    setIsProjectMustBeUpdated({
        setting: false,
        peripheral: false,
    });

    setProject(project as ProjectType);
}
